'use client';

import { useState } from 'react';
import { WaitlistDialog } from '@/components/marketing/WaitlistDialog';

/* ──────────────────────────────────────
   FEATURE DATA
   ────────────────────────────────────── */
const features = [
    {
        icon: "psychology",
        title: "Business DNA",
        description:
            "Paste your URL and our AI instantly extracts your brand voice, values, colors, and positioning into an actionable strategy.",
    },
    {
        icon: "campaign",
        title: "AI Campaigns",
        description:
            "Generate scroll-stopping copy and visuals aligned with your brand DNA — single posts or multi-image carousels.",
    },
    {
        icon: "calendar_month",
        title: "Visual Calendar",
        description:
            "Plan weeks ahead with a drag-and-drop calendar. Move, reschedule, and batch-approve content at a glance.",
    },
    {
        icon: "share",
        title: "Multi-Channel Publishing",
        description:
            "Publish directly to Google Business Profile, Facebook Pages, and Instagram Business — from a single dashboard.",
    },
];

const steps = [
    {
        number: "01",
        icon: "language",
        title: "Analyze",
        description:
            "Drop in your website URL. NectarAI scans your site and generates a complete Brand DNA — voice, tone, visuals, and audience.",
    },
    {
        number: "02",
        icon: "auto_awesome",
        title: "Generate",
        description:
            "Choose a content plan or create one-off campaigns. AI writes the copy and produces images — all on-brand, every time.",
    },
    {
        number: "03",
        icon: "rocket_launch",
        title: "Publish",
        description:
            "Schedule posts to your connected channels or let NectarAI auto-publish. Sit back and watch your brand grow.",
    },
];

/* ──────────────────────────────────────
   PAGE COMPONENT
   ────────────────────────────────────── */
export default function MarketingHomePage() {
    const [waitlistOpen, setWaitlistOpen] = useState(false);

    return (
        <>
            {/* ─── HERO ─── */}
            <section className="relative overflow-hidden">
                {/* Background layers */}
                <div className="absolute inset-0">
                    {/* Radial glow */}
                    <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[900px] h-[600px]">
                        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/20 via-emerald-500/5 to-transparent rounded-full blur-3xl" />
                    </div>
                    {/* Dot grid */}
                    <div
                        className="absolute inset-0 opacity-15"
                        style={{
                            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(16,185,129,0.35) 1px, transparent 0)`,
                            backgroundSize: "48px 48px",
                        }}
                    />
                </div>

                <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-24 pb-20 md:pt-36 md:pb-32 text-center">
                    {/* Badge */}
                    <div className="inline-flex items-center rounded-full bg-emerald-500/10 border border-emerald-500/20 px-4 py-1.5 text-sm font-medium text-emerald-400 mb-8">
                        <span className="material-symbols-outlined text-base mr-1.5 animate-status-pulse">
                            auto_awesome
                        </span>
                        AI-Powered Marketing
                    </div>

                    <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[var(--text-primary)] leading-[1.08] tracking-tight max-w-4xl mx-auto animate-ink-bleed">
                        Your brand on autopilot.{" "}
                        <span className="text-emerald-400">Powered&nbsp;by&nbsp;AI.</span>
                    </h1>

                    <p className="mt-6 text-lg md:text-xl text-[var(--text-secondary)] leading-relaxed max-w-2xl mx-auto">
                        NectarAI analyzes your website to build a Brand DNA, then generates
                        campaigns and publishes them across every channel — automatically.
                    </p>

                    {/* CTA cluster */}
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <button
                            onClick={() => setWaitlistOpen(true)}
                            className="btn-primary text-base !py-3 !px-8 shadow-lg shadow-emerald-500/20"
                        >
                            <span className="material-symbols-outlined text-xl">rocket_launch</span>
                            Join the Waitlist
                        </button>
                        <a
                            href="#how-it-works"
                            className="btn-secondary text-base !py-3 !px-8"
                        >
                            See How It Works
                        </a>
                    </div>

                    {/* Social proof nugget */}
                    <p className="mt-8 text-sm text-[var(--text-muted)]">
                        No credit card required &middot; Set up in under 2&nbsp;minutes
                    </p>
                </div>
            </section>

            {/* ─── FEATURES ─── */}
            <section id="features" className="relative py-20 md:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    {/* Section header */}
                    <div className="text-center mb-14 md:mb-20">
                        <p className="text-small text-emerald-400 mb-3">Features</p>
                        <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[var(--text-primary)]">
                            Everything you need to grow your brand
                        </h2>
                        <p className="mt-4 text-[var(--text-secondary)] max-w-xl mx-auto leading-relaxed">
                            From brand analysis to multi-channel publishing — NectarAI handles
                            the entire content lifecycle.
                        </p>
                    </div>

                    {/* Feature grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature) => (
                            <div
                                key={feature.title}
                                className="group card p-6 hover:border-emerald-500/30 hover:bg-[var(--bg-hover)] transition-all duration-300"
                            >
                                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5 group-hover:bg-emerald-500/20 transition-colors">
                                    <span className="material-symbols-outlined text-2xl text-emerald-400">
                                        {feature.icon}
                                    </span>
                                </div>
                                <h3 className="font-serif text-lg font-semibold text-[var(--text-primary)] mb-2">
                                    {feature.title}
                                </h3>
                                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── HOW IT WORKS ─── */}
            <section id="how-it-works" className="relative py-20 md:py-28 bg-[var(--bg-card)]">
                {/* Subtle top border glow */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />

                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-14 md:mb-20">
                        <p className="text-small text-emerald-400 mb-3">How It Works</p>
                        <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[var(--text-primary)]">
                            Three steps to autopilot
                        </h2>
                        <p className="mt-4 text-[var(--text-secondary)] max-w-xl mx-auto leading-relaxed">
                            Go from zero to publishing in minutes, not weeks.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-10">
                        {steps.map((step, i) => (
                            <div key={step.number} className="relative group">
                                {/* Connector line (desktop only, not after last) */}
                                {i < steps.length - 1 && (
                                    <div className="hidden md:block absolute top-10 left-[calc(50%+40px)] w-[calc(100%-40px)] h-px border-t border-dashed border-emerald-500/20" />
                                )}

                                <div className="flex flex-col items-center text-center">
                                    <div className="relative mb-6">
                                        <div className="w-20 h-20 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/20 group-hover:border-emerald-500/30 transition-all duration-300">
                                            <span className="material-symbols-outlined text-3xl text-emerald-400">
                                                {step.icon}
                                            </span>
                                        </div>
                                        <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-emerald-500 text-[#0A0A0A] text-xs font-bold flex items-center justify-center">
                                            {step.number}
                                        </span>
                                    </div>
                                    <h3 className="font-serif text-xl font-semibold text-[var(--text-primary)] mb-2">
                                        {step.title}
                                    </h3>
                                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed max-w-xs mx-auto">
                                        {step.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── CTA BANNER ─── */}
            <section className="relative py-20 md:py-28 overflow-hidden">
                {/* Glow */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px]">
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/15 via-emerald-500/5 to-transparent rounded-full blur-3xl" />
                </div>

                <div className="relative z-10 mx-auto max-w-3xl px-4 text-center">
                    <h2 className="font-serif text-3xl md:text-5xl font-bold text-[var(--text-primary)] leading-tight">
                        Ready to put your marketing on autopilot?
                    </h2>
                    <p className="mt-5 text-lg text-[var(--text-secondary)] max-w-xl mx-auto leading-relaxed">
                        Join businesses that save hours every week with AI-generated,
                        on-brand content — published automatically.
                    </p>
                    <div className="mt-10">
                        <button
                            onClick={() => setWaitlistOpen(true)}
                            className="btn-primary text-base !py-3.5 !px-10 shadow-lg shadow-emerald-500/25"
                        >
                            <span className="material-symbols-outlined text-xl">
                                auto_awesome
                            </span>
                            Join the Waitlist
                        </button>
                    </div>
                </div>
            </section>

            {/* ─── WAITLIST DIALOG ─── */}
            <WaitlistDialog
                isOpen={waitlistOpen}
                onClose={() => setWaitlistOpen(false)}
            />
        </>
    );
}
